// Pieza `commitPromotions` de «Elige el teléfono»: con el teléfono ya apartado, aparta lo marcado
// y valida el código por la fila única de la sesión. Un código rechazado no tumba la venta.
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/api/errors';
import { WIZARD_KEYS } from '@/domains/sales/wizard/api/useWizard';
import { extraLinesOf } from '@/domains/sales/wizard/buildSale';
import { anchorGiftLinesOf, saleQuoteStateOf } from '@/domains/sales/wizard/promotionSlots';
import type {
    PhoneCommitContext,
    PhoneCommitOutcome,
} from '@/domains/sales/wizard/steps/PhoneSelectStep';
import type { ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import { cachedStorePromotions, evaluationKey } from '../api/useWizardPromotions';
import type {
    EvaluatedSalePromotion,
    EvaluateSalePromotionsResponse,
    StoreSalePromotionItem,
} from '../api/wizardPromotionsContract';
import { wizardPromotionsClient } from '../api/wizardPromotions.client';
import {
    applyChange,
    reapplySelection,
    seedFromSession,
    type PromotionsApi,
    type SessionPromotionContext,
} from '../lib/applySelection';
import { withCustomerPhone } from '../lib/customerPhone';
import {
    carryDraft,
    defaultDraft,
    draftOptionsOf,
    draftSignature,
    heldSignature,
    isEmptyHeld,
    reconcileDraft,
    scopeReachesLine,
    withAnchorDraft,
    withoutUnoffered,
    type PhoneDraft,
} from '../lib/phoneDraft';
import { forAnchor, saleWithoutSureKeep } from '../lib/promotionAppliesTo';
import {
    droppedForPhoneCopy,
    errorCodeOf,
    promotionErrorCopy,
    rejectionCopy,
} from '../lib/promotionErrorCopy';
import { rememberedPromotionName } from '../lib/promotionNames';
import { rebaseOnAnchor } from '../lib/promotionSelection';
import {
    seedSessionPromotions,
    sessionPromotionState,
    storePort,
    usePromotionSelectionStore,
} from '../store/promotionSelectionStore';
import { notifyDroppedPromotions } from './droppedPromotionNotice';

export interface PhoneCommitDeps {
    sessionId: string;
    queryClient?: QueryClient;
    api?: PromotionsApi;
}

const CODE_REJECTED = 'No pudimos aplicar este código.';

/** Lo que el wizard sabe de la sesión junto a la línea del teléfono. */
export function phoneContextOf(
    session: WizardSession,
    anchorLine: ReservedLine,
): SessionPromotionContext {
    return {
        anchorLine,
        ...(session.salePromotion !== undefined && { salePromotion: session.salePromotion }),
        ...(session.salePromotions !== undefined && { salePromotions: session.salePromotions }),
        anchorGiftLines: anchorGiftLinesOf(session),
        extraLines: extraLinesOf(session),
    };
}

/** Siembra la selección con la venta fresca o la pasa al teléfono nuevo. */
function syncSelection(sessionId: string, session: WizardSession, anchorLine: ReservedLine): void {
    const context = phoneContextOf(session, anchorLine);
    const state = sessionPromotionState(sessionId);
    if (!state.synced) {
        seedSessionPromotions(sessionId, context);
        return;
    }
    if (state.selection.anchorLineId === anchorLine.lineId) return;
    const live = (session.reservedLines ?? [])
        .filter((l) => l.status !== 'RELEASING')
        .map((l) => l.lineId);
    usePromotionSelectionStore.getState().patch(sessionId, {
        selection: rebaseOnAnchor(
            state.selection,
            anchorLine.lineId,
            live,
            seedFromSession(context),
        ),
    });
}

/** Promos del teléfono en la evaluación: ocupan su lugar, le alcanzan y respetan SureKeep. */
function phonePromotionsOf(
    evaluation: EvaluateSalePromotionsResponse,
    session: WizardSession,
    anchorLine: ReservedLine,
    storeItems: readonly StoreSalePromotionItem[],
): EvaluatedSalePromotion[] {
    const scopes = new Map(storeItems.map((p) => [p.promotionId, p]));
    return forAnchor(
        evaluation.promotions.filter((p) => {
            if (!p.anchorBound) return false;
            const scoped = scopes.get(p.promotionId);
            return !scoped || scopeReachesLine(scoped, anchorLine);
        }),
        saleWithoutSureKeep(session, anchorLine),
    );
}

/** Codes con los que el servidor rechaza una promo puntual para la venta. */
const PROMOTION_REFUSALS = new Set(['PROMOTION_NOT_APPLICABLE', 'GIFT_NOT_ALLOWED']);

/** La promo que el servidor rechazó, si el rechazo dice cuál. */
function refusedPromotionOf(error: unknown): string | undefined {
    if (!(error instanceof ApiError) || !PROMOTION_REFUSALS.has(error.code)) return undefined;
    const data = (error.data ?? {}) as { promotionId?: string; details?: { promotionId?: string } };
    return data.details?.promotionId ?? data.promotionId;
}

/** El rechazo del código, por motivo. */
function codeRejectionOf(
    code: string,
    evaluation: EvaluateSalePromotionsResponse,
): { code: string; message: string } | null {
    const evaluated = evaluation.discountCode;
    if (evaluated?.applicable && evaluated.codeText === code) return null;
    return { code, message: rejectionCopy(evaluated?.rejectionCode ?? null) ?? CODE_REJECTED };
}

export function createPhonePromotionsCommit({
    sessionId,
    queryClient,
    api: baseApi = wizardPromotionsClient,
}: PhoneCommitDeps) {
    const api = withCustomerPhone(baseApi, sessionId, queryClient);
    const { patch } = usePromotionSelectionStore.getState();
    const cachedStoreItems = (): StoreSalePromotionItem[] =>
        cachedStorePromotions(sessionId, queryClient)?.items ?? [];
    const settle = (): void => {
        void queryClient?.invalidateQueries({ queryKey: evaluationKey(sessionId) });
        void queryClient?.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
    };

    /** Sin selector (kit en solo lectura): tras renovar se reaplica lo que ya tenía la venta. */
    const reapplyHeld = async (): Promise<PhoneCommitOutcome> => {
        try {
            const applied = await reapplySelection(sessionId, storePort(sessionId), api);
            notifyDroppedPromotions(sessionId, applied);
            return { stay: false };
        } catch (error) {
            toast.error(promotionErrorCopy(error));
            return { stay: true };
        } finally {
            settle();
        }
    };

    const evaluate = async (
        code: string | null,
    ): Promise<EvaluateSalePromotionsResponse | null> => {
        try {
            return await api.evaluate({ sessionId, discountCode: code });
        } catch (error) {
            toast.error(promotionErrorCopy(error));
            return null;
        }
    };

    return async ({
        session,
        anchorLineId,
        reapply,
        readOnly,
        confirmQuoteDiscard,
    }: PhoneCommitContext): Promise<PhoneCommitOutcome> => {
        const anchorLine = (session.reservedLines ?? []).find((l) => l.lineId === anchorLineId);
        if (!anchorLine) return { stay: false };
        // Con folio o cobro Promociones ya no deja cambiar nada: se sigue con lo que hay.
        if (saleQuoteStateOf(session) === 'charged') return { stay: false };
        syncSelection(sessionId, session, anchorLine);
        let draft = sessionPromotionState(sessionId).phoneDraft;
        let evaluation: EvaluateSalePromotionsResponse | null = null;
        if (!draft || draft.sku !== anchorLine.sku) {
            if (readOnly) return reapply ? reapplyHeld() : { stay: false };
            // Sin pasar por el detalle («Elegir» de la tarjeta): lo de siempre con la venta ya apartada.
            evaluation = await evaluate(draft?.code ?? null);
            if (!evaluation) return { stay: true };
            const options = draftOptionsOf(
                phonePromotionsOf(evaluation, session, anchorLine, cachedStoreItems()),
                cachedStoreItems(),
                anchorLineId,
            );
            draft = draft
                ? carryDraft(draft, anchorLine.sku, options)
                : defaultDraft(anchorLine.sku, options);
        }
        const selection = sessionPromotionState(sessionId).selection;
        const held = heldSignature(selection, anchorLineId, draft);
        const unchanged = draftSignature(draft) === held;
        if (unchanged && (!reapply || isEmptyHeld(held))) {
            patch(sessionId, { phoneDraft: draft, saleDeclined: draft.declined });
            return { stay: false };
        }
        if (!(await confirmQuoteDiscard())) return { stay: true };
        patch(sessionId, { saleDeclined: draft.declined, phoneCodeRejection: null });

        evaluation ??= await evaluate(draft.code);
        if (!evaluation) return { stay: true };
        const evaluated = evaluation;
        const { draft: reconciled, dropped } = reconcileDraft(draft, evaluated);
        const code = draft.code;
        let rejection = code ? codeRejectionOf(code, evaluation) : null;
        let unoffered: string[] = [];
        // Las que el servidor rechazó para este teléfono: se quitan y se reintenta sin ellas.
        const refused: string[] = [];
        const write = (heldCode: string | null) =>
            applyChange(
                sessionId,
                (base) => {
                    const next = withoutUnoffered(
                        withAnchorDraft(base, anchorLineId, reconciled, heldCode),
                        anchorLineId,
                        evaluated,
                        refused,
                    );
                    unoffered = next.droppedIds;
                    return next.selection;
                },
                storePort(sessionId),
                api,
            );
        const writeDroppingRefused = async (heldCode: string | null): Promise<void> => {
            for (;;) {
                try {
                    await write(heldCode);
                    return;
                } catch (error) {
                    const refusedId = refusedPromotionOf(error);
                    if (!refusedId || refused.includes(refusedId)) throw error;
                    refused.push(refusedId);
                }
            }
        };
        try {
            try {
                await writeDroppingRefused(rejection ? null : code);
            } catch (error) {
                // El código lo rechaza el PUT: se aparta lo demás y el vendedor corrige el código.
                if (!code || !errorCodeOf(error)?.startsWith('CODE_')) throw error;
                rejection = { code, message: promotionErrorCopy(error) };
                await writeDroppingRefused(null);
            }
        } catch (error) {
            toast.error(promotionErrorCopy(error, (id) => rememberedPromotionName(sessionId, id)));
            return { stay: true };
        } finally {
            settle();
        }
        for (const id of unoffered) {
            toast.info(droppedForPhoneCopy(rememberedPromotionName(sessionId, id)), {
                id: `promotion-dropped-${id}`,
            });
        }
        for (const drop of dropped) {
            toast.info(`No se pudo aplicar ${drop.name}: ${drop.reason.toLowerCase()}.`, {
                id: `promotion-dropped-${drop.promotionId}`,
            });
        }
        if (rejection) {
            patch(sessionId, {
                phoneCodeRejection: rejection,
                phoneDraft: { ...reconciled, code: null } satisfies PhoneDraft,
            });
            return { stay: true };
        }
        patch(sessionId, { phoneDraft: reconciled });
        return { stay: false };
    };
}

export type CommitPhonePromotions = ReturnType<typeof createPhonePromotionsCommit>;
